import { useLocation } from 'react-router-dom'
import { SECTION_PATHS } from '@/lib/section-paths'
import { CASE_PATHS } from '@/lib/case-slugs'

/**
 * i18n ядро сайта. Локаль определяется ПУТЁМ (URL-based), не куки/localStorage:
 *   /              → ru (дефолт)
 *   /en            → en
 *   /services/x    → ru
 *   /en/services/x → en
 *
 * Почему URL-based: обе языковые версии пре-рендерятся в SSG как отдельные
 * HTML и индексируются Яндекс/Google через hreflang. Клиентский тумблер без
 * смены URL не даёт SEO для второй версии.
 */
export type Locale = 'ru' | 'en'

export const LOCALES: Locale[] = ['ru', 'en']
export const DEFAULT_LOCALE: Locale = 'ru'

/**
 * Пути, у которых ГОТОВА английская версия. Переключатель RU/EN
 * показывается только на них, а /en-роуты заводятся только для них —
 * чтобы не отдавать полу-переведённые страницы. Расширяется по мере
 * перевода ключевых страниц.
 *   Стадия 1: только главная.
 *   Стадия 2 (2026-10-01): страницы кейсов /keysy/<slug> — данные кейсов
 *   двуязычные (src/data/case-studies.ts).
 */
export const LOCALIZED_PATHS = new Set<string>(['/', ...CASE_PATHS])

export function isLocalized(pathname: string): boolean {
  const base = stripLocale(pathname)
  // Адрес блока главной (/roi, /cases…) — та же переведённая главная:
  // переключатель языка на нём остаётся и ведёт /roi ↔ /en/roi.
  return LOCALIZED_PATHS.has(base) || base.replace(/^\/|\/$/g, '') in SECTION_PATHS
}

/** Достаёт локаль из pathname (SSR-safe, чистая функция). */
export function localeFromPath(pathname: string): Locale {
  return pathname === '/en' || pathname.startsWith('/en/') ? 'en' : 'ru'
}

/** Убирает /en-префикс — возвращает «канонический» ru-путь. */
export function stripLocale(pathname: string): string {
  if (pathname === '/en') return '/'
  if (pathname.startsWith('/en/')) return pathname.slice(3) // '/en/x' → '/x'
  return pathname
}

/** Строит путь под нужную локаль из любого текущего pathname. */
export function localizedPath(pathname: string, locale: Locale): string {
  const base = stripLocale(pathname)
  if (locale === 'ru') return base
  return base === '/' ? '/en' : `/en${base}`
}

/** Абсолютный URL под локаль (для canonical/hreflang/og). */
export function absoluteUrl(pathname: string, locale: Locale): string {
  const path = localizedPath(pathname, locale)
  return `https://dnai.engineering${path === '/' ? '/' : path}`
}

/** Хук локали текущей страницы. */
export function useLocale(): Locale {
  return localeFromPath(useLocation().pathname)
}

/** Утилита выбора значения по локали (для inline-строк в компонентах). */
export function pick<T>(locale: Locale, ru: T, en: T): T {
  return locale === 'en' ? en : ru
}
